import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, IconButton, Chip, useTheme, alpha, Rating, CircularProgress, Tooltip,
} from '@mui/material';
import { FavoriteBorder, Favorite, LocationOn, ArrowForward } from '@mui/icons-material';
import favoritesService from '../../services/favoritesService';
import { getTripImageUrl } from '../../utils/tripImage';
import { formatCurrency } from '../../utils/currency';
import { PageHeader, ButtonPill, EmptyState, hoverCardSx } from '../ui';

const MyFavorites = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [myFavorites, setMyFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await favoritesService.getFavorites();
        setMyFavorites(response.favorites || []);
      } catch {
        setMyFavorites([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRemove = async (tripId) => {
    try {
      await favoritesService.removeFromFavorites(tripId);
      setMyFavorites((prev) => prev.filter((fav) => (fav.trip?.id ?? fav.trip_id) !== tripId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Your Wishlist"
        subtitle={`${myFavorites.length} saved trip${myFavorites.length !== 1 ? 's' : ''}`}
        action={
          <ButtonPill variant="outlined" size="small" startIcon={<ArrowForward />} onClick={() => navigate('/trips')}>
            Browse more
          </ButtonPill>
        }
      />

      {myFavorites.length === 0 ? (
        <EmptyState
          icon={<FavoriteBorder sx={{ fontSize: 64 }} />}
          title="No favorites yet"
          description="Tap the heart on any trip to save it here for later."
          action={<ButtonPill variant="contained" onClick={() => navigate('/trips')}>Browse trips</ButtonPill>}
        />
      ) : (
        <Grid container spacing={2.5}>
          {myFavorites.map((fav) => {
            const trip = fav.trip || {};
            const tripId = trip.id ?? fav.trip_id ?? fav.tripId;
            return (
              <Grid item xs={12} sm={6} md={4} key={fav.id}>
                <Box sx={hoverCardSx(theme)}>
                  <Box sx={{ position: 'relative', height: 170, overflow: 'hidden', cursor: 'pointer' }} onClick={() => navigate(`/trips/${tripId}`)}>
                    <Box
                      component="img"
                      src={getTripImageUrl(trip)}
                      alt={trip.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease', '&:hover': { transform: 'scale(1.06)' } }}
                    />
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0) 55%)' }} />
                    <Chip
                      label={trip.category === 'domestic' ? 'Domestic' : 'International'}
                      size="small"
                      sx={{ position: 'absolute', top: 12, left: 12, bgcolor: alpha('#fff', 0.9), fontWeight: 700, fontSize: '0.68rem' }}
                    />
                    <Tooltip title="Remove from wishlist">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleRemove(tripId); }}
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha('#fff', 0.9), '&:hover': { bgcolor: '#fff' } }}
                      >
                        <Favorite fontSize="small" sx={{ color: theme.palette.error.main }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{trip.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }} noWrap>
                      <LocationOn sx={{ fontSize: 14 }} /> {trip.location}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.25 }}>
                      <Typography sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                        {formatCurrency(trip.price, trip.currency)}
                      </Typography>
                      <Rating value={trip.rating_average ?? trip.average_rating ?? trip.rating ?? 0} readOnly size="small" precision={0.1} />
                    </Box>
                    <ButtonPill fullWidth size="small" variant="outlined" sx={{ mt: 1.5 }} endIcon={<ArrowForward />} onClick={() => navigate(`/trips/${tripId}`)}>
                      View trip
                    </ButtonPill>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default MyFavorites;
